import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  FolderPlus,
  FolderOpen,
  File,
  FilePlus,
  ChevronRight,
  Trash2,
  RefreshCw,
  Plus,
  Pin,
  X,
} from 'lucide-react';
import axios from 'axios';

const authHeaders = () => {
  const token = localStorage.getItem('authToken') || '';
  return token ? { Authorization: `Bearer ${token}` } : {};
};

function collectPaths(nodes, out = []) {
  for (const n of nodes || []) {
    if (n?.path) out.push(n.path);
    if (n?.children?.length) collectPaths(n.children, out);
  }
  return out;
}

function TreeNode({
  node,
  depth,
  selectedPath,
  pinnedPaths,
  onOpen,
  onPin,
  expanded,
  toggle,
  appearingPaths,
}) {
  const isDir = node.type === 'dir';
  const open = expanded.has(node.path);
  const pinned = !isDir && Array.isArray(pinnedPaths) && pinnedPaths.includes(node.path);
  const appearing = appearingPaths?.has?.(node.path);

  return (
    <div className={appearing ? 'animate-tree-row-in' : undefined}>
      <div
        className={`group w-full flex items-center gap-0.5 pr-0.5 rounded-sm ${
          !isDir && selectedPath === node.path
            ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
            : 'text-neutral-600 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800/80'
        }`}
        style={{ paddingLeft: 6 + depth * 10 }}
      >
        <button
          type="button"
          onClick={() => (isDir ? toggle(node.path) : onOpen(node.path))}
          className="flex-1 min-w-0 flex items-center gap-1 px-1.5 py-1 sm:py-0.5 text-left text-[12px] sm:text-[11px] truncate touch-manipulation"
          title={node.path}
        >
          {isDir ? (
            <ChevronRight
              className={`w-3.5 h-3.5 sm:w-3 sm:h-3 flex-shrink-0 opacity-70 transition-transform duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] ${
                open ? 'rotate-90' : 'rotate-0'
              }`}
            />
          ) : (
            <File className="w-3.5 h-3.5 sm:w-3 sm:h-3 flex-shrink-0 opacity-60" />
          )}
          <span className="truncate">{node.name}</span>
        </button>
        {!isDir ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onPin?.(node.path);
            }}
            className={`flex-shrink-0 p-1.5 sm:p-0.5 rounded transition-colors touch-manipulation ${
              pinned
                ? 'text-sky-500 hover:text-sky-400 bg-sky-500/10'
                : 'text-neutral-400 hover:text-emerald-500 hover:bg-emerald-500/10'
            }`}
            title={pinned ? 'Vom Chat lösen' : 'Datei dem Chat anpinnen'}
            aria-label={pinned ? 'Vom Chat lösen' : 'Datei dem Chat anpinnen'}
          >
            {pinned ? (
              <Pin className="w-3.5 h-3.5 fill-sky-400/50" />
            ) : (
              <Plus className="w-3.5 h-3.5" />
            )}
          </button>
        ) : null}
      </div>
      {isDir ? (
        <div
          className={`grid transition-[grid-template-rows] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${
            open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
          }`}
        >
          <div className="overflow-hidden min-h-0">
            <div
              className={`transition-opacity duration-200 ease-out ${
                open ? 'opacity-100' : 'opacity-0'
              }`}
            >
              {(node.children || []).map((child) => (
                <TreeNode
                  key={child.path}
                  node={child}
                  depth={depth + 1}
                  selectedPath={selectedPath}
                  pinnedPaths={pinnedPaths}
                  onOpen={onOpen}
                  onPin={onPin}
                  expanded={expanded}
                  toggle={toggle}
                  appearingPaths={appearingPaths}
                />
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const iconBtn =
  'p-1 rounded text-neutral-400 hover:text-neutral-800 hover:bg-neutral-200/70 dark:hover:text-neutral-100 dark:hover:bg-neutral-800 disabled:opacity-35 disabled:pointer-events-none touch-manipulation';

const AgentFileTree = ({
  projectId,
  onSelectProject,
  onSelectFile,
  onPinFile,
  selectedPath,
  pinnedPaths = [],
  refreshKey = 0,
}) => {
  const [projects, setProjects] = useState([]);
  const [tree, setTree] = useState([]);
  const [expanded, setExpanded] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [newName, setNewName] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [creatingProject, setCreatingProject] = useState(false);
  /** @type {null | 'file' | 'folder'} */
  const [creatingMode, setCreatingMode] = useState(null);
  const [newFilePath, setNewFilePath] = useState('index.html');
  const [appearingPaths, setAppearingPaths] = useState(() => new Set());
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deletingProject, setDeletingProject] = useState(false);
  const newFileRef = useRef(null);
  const newProjectRef = useRef(null);
  const prevPathsRef = useRef(new Set());
  const skipAppearOnceRef = useRef(true);
  const appearClearRef = useRef(null);

  const loadProjects = async () => {
    try {
      const res = await axios.get('/api/ai/projects', { headers: authHeaders() });
      const list = res.data?.projects || [];
      setProjects(list);
      if (!projectId && list[0]?.id) {
        onSelectProject?.(list[0].id);
      } else if (projectId && !list.some((p) => String(p.id) === String(projectId))) {
        onSelectProject?.(list[0]?.id || '');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Projekte laden fehlgeschlagen');
    }
  };

  const applyTree = (nextTree, { animateNew = true } = {}) => {
    const list = nextTree || [];
    const nextPaths = new Set(collectPaths(list));
    if (animateNew && !skipAppearOnceRef.current) {
      const fresh = new Set();
      nextPaths.forEach((p) => {
        if (!prevPathsRef.current.has(p)) fresh.add(p);
      });
      if (fresh.size) {
        setAppearingPaths(fresh);
        if (appearClearRef.current) clearTimeout(appearClearRef.current);
        appearClearRef.current = setTimeout(() => setAppearingPaths(new Set()), 450);
      }
    }
    skipAppearOnceRef.current = false;
    prevPathsRef.current = nextPaths;
    setTree(list);
  };

  const loadTree = async (id, opts = {}) => {
    if (!id) {
      setTree([]);
      prevPathsRef.current = new Set();
      skipAppearOnceRef.current = true;
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`/api/ai/projects/${encodeURIComponent(id)}/tree`, {
        headers: authHeaders(),
      });
      applyTree(res.data?.tree || [], opts);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Tree fehlgeschlagen');
      setTree([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProjects();
  }, []);

  // KI legt/wechselt/benennt Projekte um → Liste oben sofort aktualisieren (ohne manuellen Refresh)
  useEffect(() => {
    loadProjects();
  }, [projectId]);

  useEffect(() => {
    const onProjectsChanged = () => {
      loadProjects();
    };
    window.addEventListener('odium:agent-projects', onProjectsChanged);
    return () => window.removeEventListener('odium:agent-projects', onProjectsChanged);
  }, []);

  useEffect(() => {
    skipAppearOnceRef.current = true;
    loadTree(projectId, { animateNew: false });
    setCreatingMode(null);
  }, [projectId, refreshKey]);

  useEffect(() => {
    if (creatingMode) {
      const t = setTimeout(() => {
        newFileRef.current?.focus();
        newFileRef.current?.select();
      }, 30);
      return () => clearTimeout(t);
    }
  }, [creatingMode]);

  useEffect(() => {
    if (!createOpen) return undefined;
    const t = setTimeout(() => {
      newProjectRef.current?.focus();
      newProjectRef.current?.select();
    }, 40);
    return () => clearTimeout(t);
  }, [createOpen]);

  useEffect(
    () => () => {
      if (appearClearRef.current) clearTimeout(appearClearRef.current);
    },
    []
  );

  const openCreateProject = () => {
    setError('');
    setNewName('');
    setCreateOpen(true);
  };

  const closeCreateProject = () => {
    if (creatingProject) return;
    setCreateOpen(false);
    setNewName('');
  };

  const createProject = async () => {
    const name = newName.trim() || 'Neues Projekt';
    setCreatingProject(true);
    setError('');
    try {
      const res = await axios.post(
        '/api/ai/projects',
        { name, rootType: 'server' },
        { headers: authHeaders() }
      );
      setNewName('');
      setCreateOpen(false);
      await loadProjects();
      if (res.data?.project?.id) onSelectProject?.(res.data.project.id);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setCreatingProject(false);
    }
  };

  const requestRemoveProject = () => {
    if (!projectId) return;
    const project = projects.find((p) => String(p.id) === String(projectId));
    setDeleteConfirm({
      id: projectId,
      name: project?.name || 'dieses Projekt',
    });
  };

  const closeDeleteConfirm = () => {
    if (deletingProject) return;
    setDeleteConfirm(null);
  };

  const confirmRemoveProject = async () => {
    if (!deleteConfirm?.id) return;
    setDeletingProject(true);
    try {
      await axios.delete(`/api/ai/projects/${encodeURIComponent(deleteConfirm.id)}`, {
        headers: authHeaders(),
      });
      setDeleteConfirm(null);
      onSelectProject?.('');
      await loadProjects();
      setTree([]);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setDeletingProject(false);
    }
  };

  const toggle = (p) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p);
      else next.add(p);
      return next;
    });
  };

  const draftBasePath = () => {
    if (!selectedPath) return '';
    const parts = String(selectedPath).replace(/\\/g, '/').split('/').filter(Boolean);
    const last = parts[parts.length - 1] || '';
    if (last.includes('.')) {
      return parts.slice(0, -1).join('/');
    }
    return parts.join('/');
  };

  const startNewFile = () => {
    if (!projectId) return;
    const dir = draftBasePath();
    setNewFilePath(dir ? `${dir}/` : 'index.html');
    setCreatingMode('file');
  };

  const startNewFolder = () => {
    if (!projectId) return;
    const dir = draftBasePath();
    setNewFilePath(dir ? `${dir}/neuer-ordner` : 'neuer-ordner');
    setCreatingMode('folder');
  };

  const expandParentsOf = (relPath) => {
    const parts = String(relPath).replace(/\\/g, '/').split('/').filter(Boolean);
    setExpanded((prev) => {
      const next = new Set(prev);
      let acc = '';
      for (let i = 0; i < parts.length - 1; i += 1) {
        acc = acc ? `${acc}/${parts[i]}` : parts[i];
        next.add(acc);
      }
      return next;
    });
  };

  const commitNewEntry = async () => {
    const mode = creatingMode;
    let path = String(newFilePath || '')
      .replace(/\\/g, '/')
      .replace(/^\/+/, '')
      .trim();

    if (!path) {
      setCreatingMode(null);
      return;
    }

    const asFolder = mode === 'folder' || path.endsWith('/');
    path = path.replace(/\/+$/, '');

    if (!path) {
      setCreatingMode(null);
      return;
    }

    setCreatingMode(null);

    if (asFolder) {
      try {
        await axios.post(
          `/api/ai/projects/${encodeURIComponent(projectId)}/mkdir`,
          { path },
          { headers: authHeaders() }
        );
        expandParentsOf(`${path}/_`);
        await loadTree(projectId, { animateNew: true });
      } catch (err) {
        setError(err.response?.data?.error || err.message || 'Ordner anlegen fehlgeschlagen');
      }
      return;
    }

    expandParentsOf(path);
    onSelectFile?.(path, { create: true });
  };

  const cancelNewEntry = () => {
    setCreatingMode(null);
  };

  return (
    <div className="h-full min-h-0 flex flex-col border-r border-neutral-200 dark:border-neutral-800 bg-neutral-50/80 dark:bg-neutral-950">
      <div className="flex-shrink-0 px-2 py-2 border-b border-neutral-200 dark:border-neutral-800 space-y-2">
        <div className="flex items-center justify-between gap-1">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-neutral-500">
            Projekte
          </span>
          <div className="flex items-center gap-0.5">
            <button
              type="button"
              onClick={requestRemoveProject}
              disabled={!projectId}
              className={iconBtn}
              title="Projekt löschen"
              aria-label="Projekt löschen"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {projects.length > 0 ? (
          <select
            value={projectId || ''}
            onChange={(e) => onSelectProject?.(e.target.value)}
            className="input-field w-full text-[11px] !py-1.5 !pl-2 !pr-6"
            aria-label="Projekt wählen"
          >
            <option value="">Projekt wählen…</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        ) : null}

        <button
          type="button"
          onClick={openCreateProject}
          className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-[11px] font-medium px-2.5 py-2 transition-colors touch-manipulation"
        >
          <FolderPlus className="w-3.5 h-3.5" />
          Neues Projekt
        </button>
      </div>

      <div className="flex-shrink-0 flex items-center gap-0.5 px-1.5 py-1 border-b border-neutral-200/80 dark:border-neutral-800">
        <span className="flex-1 min-w-0 px-1 text-[10px] font-semibold uppercase tracking-wide text-neutral-500 truncate">
          Dateien
        </span>
        <button
          type="button"
          disabled={!projectId}
          onClick={startNewFile}
          className={iconBtn}
          title="Neue Datei"
          aria-label="Neue Datei"
        >
          <FilePlus className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          disabled={!projectId}
          onClick={startNewFolder}
          className={iconBtn}
          title="Neuer Ordner"
          aria-label="Neuer Ordner"
        >
          <FolderPlus className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          disabled={!projectId}
          onClick={() => {
            loadProjects();
            if (projectId) loadTree(projectId);
          }}
          className={iconBtn}
          title="Aktualisieren"
          aria-label="Aktualisieren"
        >
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto py-0.5">
        {creatingMode ? (
          <div className="animate-tree-enter flex items-center gap-1 px-1.5 py-1 mx-0.5 mb-0.5 rounded-sm bg-neutral-100 dark:bg-neutral-900 border border-emerald-500/30">
            {creatingMode === 'folder' ? (
              <FolderOpen className="w-3 h-3 flex-shrink-0 text-emerald-500/80" />
            ) : (
              <File className="w-3 h-3 flex-shrink-0 text-neutral-400" />
            )}
            <input
              ref={newFileRef}
              value={newFilePath}
              onChange={(e) => setNewFilePath(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitNewEntry();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  cancelNewEntry();
                }
              }}
              onBlur={() => {
                setTimeout(() => {
                  if (document.activeElement !== newFileRef.current) commitNewEntry();
                }, 120);
              }}
              placeholder={creatingMode === 'folder' ? 'ordner/pfad' : 'pfad/datei.ext'}
              className="flex-1 min-w-0 bg-transparent border-0 outline-none text-[11px] font-mono text-neutral-800 dark:text-neutral-100 placeholder:text-neutral-500"
              aria-label={creatingMode === 'folder' ? 'Ordnerpfad' : 'Dateipfad'}
            />
          </div>
        ) : null}

        {loading ? (
          <p className="px-2 py-1 text-[11px] text-neutral-400">Lädt…</p>
        ) : !projectId ? (
          <div className="px-3 py-6 text-center space-y-2">
            <FolderOpen className="w-6 h-6 mx-auto text-neutral-400" />
            <p className="text-[11px] text-neutral-500 leading-relaxed">
              {projects.length === 0
                ? 'Noch kein Projekt. Lege eines an, dann kann der Agent darin arbeiten.'
                : 'Wähle oben ein Projekt oder erstelle ein neues.'}
            </p>
          </div>
        ) : tree.length === 0 && !creatingMode ? (
          <button
            type="button"
            onClick={startNewFile}
            className="mx-1.5 mt-1 w-[calc(100%-0.75rem)] rounded-md border border-dashed border-neutral-300 dark:border-neutral-700 px-2 py-3 text-[11px] text-neutral-500 hover:border-emerald-500/40 hover:text-emerald-600 dark:hover:text-emerald-300 transition-colors"
          >
            Noch leer — Datei anlegen
          </button>
        ) : (
          <>
            {tree.map((node) => (
              <TreeNode
                key={node.path}
                node={node}
                depth={0}
                selectedPath={selectedPath}
                pinnedPaths={pinnedPaths}
                onOpen={onSelectFile}
                onPin={onPinFile}
                expanded={expanded}
                toggle={toggle}
                appearingPaths={appearingPaths}
              />
            ))}
          </>
        )}
      </div>

      {error && !createOpen ? (
        <p className="flex-shrink-0 px-2 py-1 text-[10px] text-rose-600 dark:text-rose-300 border-t border-neutral-200 dark:border-neutral-800">
          {error}
        </p>
      ) : null}

      {createOpen &&
        createPortal(
          <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
            <div
              className="absolute inset-0 bg-neutral-900/60 backdrop-blur-sm"
              onClick={closeCreateProject}
            />
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="create-project-title"
              className="relative bg-white dark:bg-neutral-900 rounded-2xl shadow-xl w-full max-w-sm overflow-hidden border border-neutral-200 dark:border-neutral-800 scale-in"
            >
              <div className="px-5 pt-5 pb-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-xl bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 flex items-center justify-center flex-shrink-0">
                      <FolderPlus className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <h3
                        id="create-project-title"
                        className="text-base font-semibold text-neutral-900 dark:text-white"
                      >
                        Neues Projekt
                      </h3>
                      <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">
                        Nur einen Namen vergeben — fertig.
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={closeCreateProject}
                    disabled={creatingProject}
                    className="p-1.5 rounded-lg text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 dark:hover:text-neutral-200 dark:hover:bg-neutral-800 disabled:opacity-50"
                    aria-label="Schließen"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <label className="mt-4 block">
                  <span className="sr-only">Projektname</span>
                  <input
                    ref={newProjectRef}
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        createProject();
                      } else if (e.key === 'Escape') {
                        e.preventDefault();
                        closeCreateProject();
                      }
                    }}
                    placeholder="z. B. Landingpage"
                    disabled={creatingProject}
                    className="input-field w-full text-sm !py-2.5 !px-3"
                    maxLength={80}
                    autoComplete="off"
                  />
                </label>
                {error ? (
                  <p className="mt-2 text-xs text-rose-600 dark:text-rose-300">{error}</p>
                ) : null}
              </div>
              <div className="px-5 py-3 border-t border-neutral-100 dark:border-neutral-800 flex flex-row-reverse gap-2 bg-neutral-50/80 dark:bg-neutral-950/40">
                <button
                  type="button"
                  disabled={creatingProject}
                  onClick={createProject}
                  className="inline-flex items-center justify-center rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium px-3.5 py-2 disabled:opacity-60"
                >
                  {creatingProject ? 'Erstellen…' : 'Erstellen'}
                </button>
                <button
                  type="button"
                  disabled={creatingProject}
                  onClick={closeCreateProject}
                  className="inline-flex items-center justify-center rounded-lg border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 text-sm font-medium px-3.5 py-2 hover:bg-white dark:hover:bg-neutral-800 disabled:opacity-60"
                >
                  Abbrechen
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {deleteConfirm &&
        createPortal(
          <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
            <div
              className="absolute inset-0 bg-neutral-900/60 backdrop-blur-sm"
              onClick={closeDeleteConfirm}
            />
            <div className="relative bg-white dark:bg-neutral-900 rounded-2xl shadow-xl w-full max-w-sm overflow-hidden border border-neutral-200 dark:border-neutral-800 scale-in">
              <div className="px-5 pt-5 pb-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-danger-50 dark:bg-danger-900/30 text-danger-600 dark:text-danger-400 flex items-center justify-center flex-shrink-0">
                    <Trash2 className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold text-neutral-900 dark:text-white">
                      Projekt löschen?
                    </h3>
                    <p className="mt-1.5 text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">
                      Möchtest du das Projekt{' '}
                      <span className="font-medium text-neutral-900 dark:text-neutral-100">
                        „{deleteConfirm.name}“
                      </span>{' '}
                      wirklich löschen? Alle Dateien darin gehen verloren.
                    </p>
                  </div>
                </div>
              </div>
              <div className="px-5 py-3 border-t border-neutral-100 dark:border-neutral-800 flex flex-row-reverse gap-2 bg-neutral-50/80 dark:bg-neutral-950/40">
                <button
                  type="button"
                  disabled={deletingProject}
                  onClick={confirmRemoveProject}
                  className="inline-flex items-center justify-center rounded-lg bg-danger-600 hover:bg-danger-700 text-white text-sm font-medium px-3.5 py-2 disabled:opacity-60"
                >
                  {deletingProject ? 'Löschen…' : 'Löschen'}
                </button>
                <button
                  type="button"
                  disabled={deletingProject}
                  onClick={closeDeleteConfirm}
                  className="inline-flex items-center justify-center rounded-lg border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 text-sm font-medium px-3.5 py-2 hover:bg-white dark:hover:bg-neutral-800 disabled:opacity-60"
                >
                  Abbrechen
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default AgentFileTree;
